import RightArrowIcon from 'app/components/icons/RightArrowIcon';
import UnreadMessagesBadge from 'app/components/UnreadMessagesBadge';
import { AnimatePresence, motion } from 'framer-motion';
import { ReactNode, useState } from 'react';
import { BubbleMessage } from 'app/copied/types/ChatItems';
import { cx } from '@twind/core';
import CloseButton from 'app/components/Header/CloseButton';

import BubbleMessages from './BubbleMessages';

const buttonVariants = {
  lg: {
    initial: {
      height: 52,
      borderRadius: '6px',
    },
    expanded: {
      height: 68,
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      transition: { duration: 1, ease: [0.25, 0.1, 0.25, 1] },
    },
    collapsed: {
      height: 52,
      borderTopLeftRadius: '6px',
      borderBottomLeftRadius: '6px',
      transition: {
        duration: 1,
        ease: [0.25, 0.1, 0.25, 1],
        borderTopLeftRadius: { delay: 0.4 },
        borderBottomLeftRadius: { delay: 0.4 },
      },
    },
  },
  md: {
    initial: {
      height: 52,
      borderRadius: '6px',
    },
    expanded: {
      height: 52,
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      transition: { duration: 1, ease: [0.25, 0.1, 0.25, 1] },
    },
    collapsed: {
      height: 52,
      borderTopLeftRadius: '6px',
      borderBottomLeftRadius: '6px',
      transition: {
        duration: 1,
        ease: [0.25, 0.1, 0.25, 1],
        borderTopLeftRadius: { delay: 0.4 },
        borderBottomLeftRadius: { delay: 0.4 },
      },
    },
  },
};

const GenericV2Button = ({
  id,
  onClick,
  onClickCloseChatBubble,
  prependText,
  messages,
  actionIcon,
  buttonIcon,
  actionIconSize,
  buttonIconSize,
  isExpanded,
  expandedSize = 'lg',
  unreadMessageCount,
  messageLineClamp = 1,
  textClassName,
}: {
  id: string;
  onClick: () => void;
  onClickCloseChatBubble: () => void;
  prependText?: ReactNode;
  messages: BubbleMessage[];
  actionIcon?: string;
  buttonIcon: string;
  actionIconSize: { width: number; height: number };
  buttonIconSize: { width: number; height: number };
  isExpanded: boolean;
  expandedSize?: 'lg' | 'md';
  unreadMessageCount: number;
  messageLineClamp?: number;
  textClassName?: string;
}) => {
  const width = 270;
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);
  return (
    <div
      id={id}
      className="flex items-center justify-end widget-position-mobile md:widget-position-desktop pointer-events-auto"
    >
      <AnimatePresence mode="wait">
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, width: 0, height: 52 }}
            animate={{
              opacity: 1,
              width,
              height: expandedSize === 'lg' ? 68 : 52,
            }}
            exit={{ opacity: 0, width: 0, height: 52 }}
            transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-brand-openBubbleBackground rounded-l-md shadow-lg cursor-pointer button-width-mobile border-l-buttonColor border-l-buttonWidth border-y-buttonColor border-y-buttonWidth"
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
          >
            <CloseButton
              id="ostro-webchat-close-text-button"
              onClick={(e) => {
                e.stopPropagation();
                onClickCloseChatBubble();
              }}
              circleClassName="bg-white w-6 h-6 shadow-lg"
              className="absolute top-[-10px] left-[-10px] w-6 h-6"
              useCustomIcon
              closeIconClassName="text-black fill-black"
            />
            <motion.div
              className="overflow-hidden h-full flex items-center p-4"
              initial={{ whiteSpace: 'nowrap' }}
              animate={{
                whiteSpace: 'normal',
                transition: { delay: 0.8 },
              }}
              exit={{
                whiteSpace: 'nowrap',
                transition: { duration: 0 },
              }}
              onAnimationComplete={() => {
                setIsAnimationComplete(true);
              }}
              onAnimationStart={() => {
                setIsAnimationComplete(false);
              }}
            >
              <BubbleMessages
                id="ostro-webchat-button-text"
                prependText={prependText}
                messages={messages}
                className="[&_p]:(mb-0 inline) min-w-fit"
                textClassName={`line-clamp-${messageLineClamp} ${textClassName}`}
                subtextClassName={`${isAnimationComplete ? `line-clamp-${messageLineClamp}` : ''}`}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <motion.button
        onClick={onClick}
        variants={buttonVariants[expandedSize]}
        initial="initial"
        animate={isExpanded ? 'expanded' : 'collapsed'}
        className={cx(
          '~(bg-brand-openButtonBackground shadow-lg flex items-center justify-center rounded-r-md border-r-buttonColor border-r-buttonWidth border-y-buttonColor border-y-buttonWidth h-[52px] w-[52px])',
          isExpanded && expandedSize === 'lg' && 'h-[68px]',
          !isExpanded && 'border-l-buttonColor border-l-buttonWidth'
        )}
      >
        {isExpanded ? (
          <>
            {actionIcon ? (
              <img
                className={`w-[${actionIconSize.width}px] h-[${actionIconSize.height}px]`}
                src={actionIcon}
                alt="Action Icon"
              />
            ) : (
              <RightArrowIcon className="w-4 h-4" />
            )}
          </>
        ) : (
          <img
            className={`w-[${buttonIconSize.width}px] h-[${buttonIconSize.height}px]`}
            src={buttonIcon}
            alt="Button Icon"
          />
        )}
        {unreadMessageCount > 0 && (
          <UnreadMessagesBadge
            count={unreadMessageCount}
            size="md"
            className="top-[-5px] right-[-5px]"
          />
        )}
      </motion.button>
    </div>
  );
};

export default GenericV2Button;
